// Pure adapters: api-core C1/C2 JSON  →  the frozen Vuex/component shapes the /play
// components still consume (int statuses, 0/1 teams, EAV config rows, userId/userName
// players). No axios, no store, no browser APIs — so these run under plain node
// (test/apicoreAdapters.test.mjs) exactly like test/matchId.test.mjs.
//
// Contract: docs/superpowers/plans/2026-07-11-gathermig-00-index.md (C1/C2) with the
// R-series corrections (R13 player_count, R15 nullable name, R16 map_selection type,
// R17 ISO-UTC dates sort lexically).

// --- enums (C1) ---
export function statusToInt(s) {
  switch (s) {
    case 'open':
      return 0;
    case 'live':
      return 1;
    case 'finished':
      return 2;
    case 'cancelled':
      return -1;
    default:
      // Unknown → -1 so Info.vue's status ladder falls to its "cancelled" else branch.
      return -1;
  }
}

export function teamToInt(t) {
  // Legacy default team is 0; C2 allows null (unassigned) which also maps to 0.
  return t === 'team2' ? 1 : 0;
}

export function intToTeam(n) {
  return n === 1 ? 'team2' : 'team1';
}

// --- players ---
export function abbreviateSnowflake(id) {
  const s = id === undefined || id === null ? '' : String(id);
  return s ? '…' + s.slice(-5) : '';
}

export function fallbackName(p) {
  // R15: Player.name is nullable; fall back to an abbreviated snowflake only when null.
  if (p && p.name !== undefined && p.name !== null && p.name !== '') return p.name;
  return abbreviateSnowflake(p ? p.discord_id : '');
}

export function adaptPlayer(p, names) {
  const discordId = p && p.discord_id !== undefined && p.discord_id !== null ? String(p.discord_id) : '';
  const overridden = names && names[discordId];
  return {
    userId: discordId,
    userName: overridden || fallbackName(p),
    team: teamToInt(p ? p.team : null),
    ready: p && p.ready ? 1 : 0,
    // kept only for the stable sort below (joined_at is ISO-UTC → lexical order, R17).
    joined_at: p ? p.joined_at : undefined,
  };
}

export function sortAdaptedPlayers(list) {
  return (list || []).slice().sort((a, b) => {
    if (a.team !== b.team) return a.team - b.team;
    const aj = a.joined_at || '';
    const bj = b.joined_at || '';
    if (aj < bj) return -1;
    if (aj > bj) return 1;
    return 0;
  });
}

export function adaptPlayers(list, names) {
  return sortAdaptedPlayers((list || []).map((p) => adaptPlayer(p, names)));
}

export function upsertAdaptedPlayer(list, adapted) {
  const rest = (list || []).filter((p) => p.userId !== adapted.userId);
  rest.push(adapted);
  return sortAdaptedPlayers(rest);
}

// --- config (EAV) ---
export function normalizeConfigValue(key, value) {
  // Backfilled/seed values are rich EAV objects {name,type,value,options?} (R3) — pass
  // through untouched so components read .value.value and api-core keeps options on
  // write-back. A bare scalar gets wrapped so .value.value stays uniform (F8).
  if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    return value;
  }
  return { name: key, type: 'text', value: value };
}

const byKeyAsc = (a, b) => (a.key > b.key ? 1 : b.key > a.key ? -1 : 0);

export function adaptConfig(obj) {
  const src = obj || {};
  return Object.keys(src)
    .map((key) => ({ key, value: normalizeConfigValue(key, src[key]) }))
    .sort(byKeyAsc);
}

export function upsertConfigRow(eav, key, value) {
  const rows = (eav || []).filter((r) => r.key !== key);
  rows.push({ key, value: normalizeConfigValue(key, value) });
  return rows.sort(byKeyAsc);
}

// --- gather info / list ---
export function displayName(g) {
  // F6: no channel_name in the schema today → derive "Gather #<gather_seq>"
  // (consistent with C5's voice/card "(#<gather_seq>)" naming). Prefer a real name if present.
  if (g && g.channel_name) return g.channel_name;
  return 'Gather #' + (g ? g.gather_seq : '');
}

export function adaptGatherInfo(g, playerCount) {
  const size = g && g.max_players ? g.max_players : 0;
  return {
    id: g ? g.id : 0,
    leader: g && g.leader_discord_id !== undefined && g.leader_discord_id !== null ? String(g.leader_discord_id) : '',
    name: displayName(g),
    token: g ? g.match_id : '',
    joinedPlayers: playerCount,
    size: size,
    size0: size / 2,
    size1: size / 2,
  };
}

export function adaptListItem(g) {
  return {
    matchId: g ? g.match_id : '',
    channelName: displayName(g),
    // R13: Gather carries player_count in list/by-token/snapshot. '?' only if absent.
    joinedPlayers: g && g.player_count !== undefined && g.player_count !== null ? g.player_count : '?',
    maxPlayers: g ? g.max_players : 0,
  };
}

// --- maps ---
export function adaptMaps(groups) {
  // C2 available-maps: {groups:[{name, maps:[string]}]} → the grouped shape SettingsGather
  // reads. workshopId is absent (R5); SettingsMaps.vue already guards v-if="map.workshopId".
  return (groups || []).map((group, index) => ({
    mapGroupId: group.name || index,
    name: group.name,
    maps: (group.maps || []).map((mapName) => ({ mapId: mapName, name: mapName, workshopId: undefined })),
  }));
}

// --- server ---
export function serverStateFromStatus(intStatus, connectString) {
  // Mirrors legacy gather.js:563-565 booleans off the int status.
  return {
    status: intStatus,
    started: intStatus === 1,
    locked: intStatus !== 0,
    connectString: connectString !== undefined && connectString !== null ? connectString : '',
  };
}

// --- WS / reconnect ---
export function wsLobbyUrl(origin, token) {
  // Pure core of the lobby WS URL (https→wss, /ws/gather/{token}). Auth is NOT in the
  // URL — it rides Sec-WebSocket-Protocol (R9); the token never appears here.
  return String(origin).replace(/^http/, 'ws') + '/ws/gather/' + token;
}

export function nextBackoffMs(attempt) {
  const base = Math.min(1000 * Math.pow(2, attempt), 30000);
  const jitter = Math.floor(Math.random() * 500);
  return base + jitter;
}

// --- LAN challenge tick (addendum 2026-09-24 §6, api-core contract §4) ---
// api-core owns the visibility rule (leader ∧ crew ∧ LAN window running ∧ gather open);
// the page only renders its answer. Anything that is not an explicit `true` is hidden /
// unticked, so a malformed or partial response can never surface the checkbox.
export function adaptLanChallenges(body) {
  const visible = !!body && body.visible === true;
  return { visible, ticked: visible && body.ticked === true };
}

// Whether the page should ask api-core at all. Only the leader of an open gather can
// ever be shown the box (contract §4), so everyone else is hidden without a request.
// This gate only ever HIDES; it never decides that the box is shown. intStatus is the
// statusToInt value (0 = open).
export function shouldQueryLanChallenges(gatherId, me, leader, intStatus) {
  const meId = me === undefined || me === null ? '' : String(me);
  const leaderId = leader === undefined || leader === null ? '' : String(leader);
  return !!gatherId && meId !== '' && meId === leaderId && intStatus === 0;
}

// player_left's `leader_discord_id` (contract rev 2 §4): the leader after the leave, as a
// string ('' when the gather has no players left → null leader). undefined when the field
// is absent (an api-core older than rev 2) so the caller can fall back to a re-read.
export function leaderAfterLeave(data) {
  if (!data || !Object.prototype.hasOwnProperty.call(data, 'leader_discord_id')) return undefined;
  const l = data.leader_discord_id;
  return l === null || l === undefined ? '' : String(l);
}

// Ordering for the tick's GET/PUT. A read only lands if no newer read was issued AND no
// write started or was in flight since it was issued (it may have read the pre-tick row);
// a write only lands if it is the newest write. So a snapshot-fired GET can never undo a
// tick. invalidate() drops everything in flight (gather page left).
export function createTickRequestOrder() {
  let readSeq = 0;
  let writeSeq = 0;
  let writesInFlight = 0;
  return {
    beginRead() {
      const read = ++readSeq;
      const write = writeSeq;
      // Issued mid-write → it may have raced the write on the server; never lands.
      const cleanStart = writesInFlight === 0;
      return () => cleanStart && read === readSeq && write === writeSeq && writesInFlight === 0;
    },
    beginWrite() {
      const write = ++writeSeq;
      writesInFlight++;
      let ended = false;
      return {
        current: () => write === writeSeq,
        end() {
          if (!ended) {
            ended = true;
            writesInFlight--;
          }
        },
      };
    },
    writing: () => writesInFlight > 0,
    invalidate() {
      readSeq++;
      writeSeq++;
    },
  };
}

// The contract §4 `detail` strings → language keys. Unknown detail → null (the caller
// falls back to the generic error text).
const LAN_CHALLENGE_ERRORS = {
  not_leader: 'lanChallengesNotAllowed',
  not_crew: 'lanChallengesNotAllowed',
  not_open: 'lanChallengesNotOpen',
  no_lan_window: 'lanChallengesNoWindow',
  leader_changed: 'lanChallengesLeaderChanged',
};

export function lanChallengesErrorKey(status, detail) {
  if ((status === 403 || status === 409) && Object.prototype.hasOwnProperty.call(LAN_CHALLENGE_ERRORS, detail)) {
    return LAN_CHALLENGE_ERRORS[detail];
  }
  return null;
}
